import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { SmartTeamsAppUser } from '@nsc/smartteams-shared/db/SmartTeamsAppUser.js';
import { SmartTeamsFieldEngineer } from '@nsc/smartteams-shared/db/SmartTeamsFieldEngineer.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { SmartTeamsPermission } from '../features/permissions/roles-and-permissions.js';

/**
 * Diagnostic snapshot of a user's SmartTeams app access — surfaced by the admin user
 * management section so an admin can quickly verify whether a legacy/academy user is
 * properly onboarded as FE / Customer / etc., and toggle the archived flag.
 */
@Entity()
export class SmartTeamsAppAccessStatus extends AdornisEntity {
  static override _class = 'SmartTeamsAppAccessStatus';

  /** True if a SmartTeamsAppUser record exists for this AdornisUser. */
  @Field(() => Boolean, { default: v => v ?? false })
  hasAppAccess: Maybe<boolean>;

  /** "Admin" / "FieldEngineer" / "Customer" / "PartnerManager" — undefined when no app user. */
  @Field(() => String)
  userGroup: Maybe<string>;

  @Field(() => Boolean, { default: v => v ?? false })
  archived: Maybe<boolean>;

  @Field(() => Boolean, { default: v => v ?? false })
  mustChangePassword: Maybe<boolean>;

  /** Linked ServiceNow FE sys_id (FieldEngineer userGroup only). */
  @Field(() => String)
  serviceNowFEId: Maybe<string>;

  /** Whether a SmartTeamsFieldEngineer record points at this userId. Catches FE login set up
   *  without a FE record (the FE app would let them in but show no tickets). */
  @Field(() => Boolean, { default: v => v ?? false })
  hasFieldEngineerRecord: Maybe<boolean>;

  /** Display name from SmartTeamsFieldEngineer when present. */
  @Field(() => String)
  fieldEngineerName: Maybe<string>;

  /** Number of linked SmartTeamsCustomer records (Customer userGroup only). */
  @Field(() => Int, { default: v => v ?? 0 })
  linkedCustomerCount: Maybe<number>;
}

/**
 * Returns the access snapshot for any AdornisUser. UserManage-gated.
 */
export const getAppAccessStatus = registerQuery({
  operationName: 'getAppAccessStatus',
  type: () => SmartTeamsAppAccessStatus,
  params: [{ name: 'userId', type: () => String }],
  options: { requestHandlerMeta: { queryCache: { cachePolicy: 'bypass' } } },
  resolve: (userId: string) => async (_gql: BaseQLSelectionSet<SmartTeamsAppAccessStatus>) => {
    const allowed = await accessController.can(createPermissionPayload(SmartTeamsPermission.UserManage));
    if (!allowed) throw new Error('No permission to read app access status');

    const appUserCol = await getRawCollection<SmartTeamsAppUser>(SmartTeamsAppUser._collectionName);
    const appUser = await appUserCol.findOne({ userId });

    if (!appUser) {
      return new SmartTeamsAppAccessStatus({
        hasAppAccess: false,
        archived: false,
        mustChangePassword: false,
        hasFieldEngineerRecord: false,
        linkedCustomerCount: 0,
      });
    }

    // FE record diagnostic — useful when the user is in FieldEngineer userGroup but no
    // SmartTeamsFieldEngineer row exists (orphan login). Also nice to show the FE name.
    const feCol = await getRawCollection<SmartTeamsFieldEngineer>(SmartTeamsFieldEngineer._collectionName);
    const fe = await feCol.findOne({ userId }, { projection: { _id: 1, name: 1, mustChangePassword: 1 } });

    return new SmartTeamsAppAccessStatus({
      hasAppAccess: true,
      userGroup: appUser.userGroup ?? null,
      archived: appUser.archived ?? false,
      // mustChangePassword lives on the FE record, not the app user — surface it for visibility.
      mustChangePassword: fe?.mustChangePassword ?? false,
      serviceNowFEId: appUser.serviceNowFEId ?? null,
      hasFieldEngineerRecord: !!fe,
      fieldEngineerName: fe?.name ?? null,
      linkedCustomerCount: (appUser.customerIds ?? []).length,
    });
  },
});

/**
 * Toggle the SmartTeamsAppUser.archived flag. Archived users are blocked at login by the
 * FE app's `validateUser` hook. UserManage-gated.
 */
export const setAppUserArchived = registerMutation({
  operationName: 'setAppUserArchived',
  type: () => Boolean,
  params: [
    { name: 'userId', type: () => String },
    { name: 'archived', type: () => Boolean },
  ],
  resolve: (userId: string, archived: boolean) => async () => {
    const allowed = await accessController.can(createPermissionPayload(SmartTeamsPermission.UserManage));
    if (!allowed) throw new Error('No permission to archive app users');

    const col = await getRawCollection<SmartTeamsAppUser>(SmartTeamsAppUser._collectionName);
    const result = await col.updateOne({ userId }, { $set: { archived, updatedAt: new Date() } });
    if (result.matchedCount === 0) throw new Error('User has no app access — nothing to archive');
    return true;
  },
});
